import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, BadgeCheck, CalendarClock, Check, CreditCard, Hourglass, Sparkles } from 'lucide-react';
import { useMe } from '@/api/auth';
import { ApiError } from '@/api/client';
import {
  useCancelMembership, useChangeMembershipPlan, useStartMembership, type MembershipSubject,
} from '@/api/memberships';
import type {
  BillingInterval, MeMembershipDto, MembershipBuyerDto, MembershipBuyerRequest, MembershipPageDto, MembershipPaymentDto, MembershipPlanDto,
  MembershipState, MembershipStatusDto,
} from '@/api/types';
import { areaOfPath, canManage, useCurrentOrg } from '@/auth/roles';
import { Badge, type Tone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog, Dialog } from '@/components/ui/Dialog';
import { Field, FormError, Input } from '@/components/ui/Field';
import { Section } from '@/components/ui/Page';
import { Segmented } from '@/components/ui/Segmented';
import { Money } from '@/components/domain/Market';
import { cn } from '@/lib/cn';
import { errorMessage, toastError } from '@/lib/errors';
import { formatDate, formatMoney } from '@/lib/format';

const STATE_TONE: Record<MembershipState, Tone> = {
  NotStarted: 'muted', Trial: 'info', Active: 'trusted', PastDue: 'check', Cancelled: 'muted', Granted: 'accent', Expired: 'suspicious',
};

/** Access given "for good" by the platform (the academy) ends far in the future. */
export const isForever = (iso: string | null | undefined) => !!iso && new Date(iso).getUTCFullYear() >= 9000;

export function MembershipStateBadge({ state, className }: { state: MembershipState; className?: string }) {
  const { t } = useTranslation();
  return <Badge tone={STATE_TONE[state]} className={className}>{t(`membership.state.${state}`)}</Badge>;
}

/** Where a subject's membership page is. */
export const membershipPath = (subject: { audience: 'Organization' | 'Tester'; organizationId: string | null }) =>
  subject.audience === 'Organization' ? `/org/membership${subject.organizationId ? `?org=${subject.organizationId}` : ''}` : '/my/membership';

/** One sentence on where the membership stands (trial days left, next renewal, when access ends…). */
function statusLine(s: MembershipStatusDto, t: (k: string, o?: Record<string, unknown>) => string, date: (v: string | null) => string): string {
  const forTester = s.audience === 'Tester';
  switch (s.state) {
    case 'NotStarted':
      return t('membership.line.NotStarted', { days: s.trialDays });
    case 'Trial':
      return t('membership.line.Trial', { date: date(s.accessUntil), count: s.daysLeft ?? 0 });
    case 'Active': {
      const plan = s.plan ? `${s.plan.name} · ${t(`membership.interval.${s.plan.interval}`)}` : '';
      return t('membership.line.Active', { plan, date: date(s.paidThrough) });
    }
    case 'PastDue':
      return t('membership.line.PastDue', { date: date(s.accessUntil) });
    case 'Cancelled':
      return s.entitled ? t('membership.line.CancelledOpen', { date: date(s.accessUntil) }) : t('membership.line.Expired');
    case 'Granted':
      return isForever(s.accessUntil) ? t('membership.line.GrantedForever') : t('membership.line.Granted', { date: date(s.accessUntil) });
    case 'Expired':
      return forTester ? t('membership.line.ExpiredTester') : t('membership.line.Expired');
  }
}

const EMPTY_BUYER: MembershipBuyerRequest = { name: '', surname: '', identityNumber: '', phone: '', address: '', city: '' };

/** A tester pays as a person: the invoice and the card provider need their name, T.C. identity number, phone and address. */
function BuyerDialog({ plan, saved, open, onOpenChange }: {
  plan: MembershipPlanDto; saved: MembershipBuyerDto | null; open: boolean; onOpenChange: (o: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const start = useStartMembership({ kind: 'me' });
  const [buyer, setBuyer] = useState<MembershipBuyerRequest>(() =>
    saved ? { name: saved.name, surname: saved.surname, identityNumber: '', phone: saved.phone, address: saved.address, city: saved.city } : EMPTY_BUYER);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const set = (k: keyof MembershipBuyerRequest) => (e: React.ChangeEvent<HTMLInputElement>) => setBuyer((b) => ({ ...b, [k]: e.target.value }));
  const submit = () => {
    setError(null);
    setFieldErrors({});
    start.mutate({ planId: plan.id, buyer }, {
      onSuccess: (r) => window.location.assign(r.checkoutUrl),
      onError: (e) => {
        const next: Record<string, string> = {};
        if (e instanceof ApiError && e.status === 400)
          for (const [k, v] of Object.entries(e.fieldErrors)) if (k.startsWith('buyer.')) next[k.slice('buyer.'.length)] = v[0] ?? '';
        setFieldErrors(next);
        setError(Object.keys(next).length > 0 ? null : errorMessage(e));
      },
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="md" modalLock
      title={t('membership.buyerTitle')}
      description={t('membership.buyerHint', { plan: plan.name, interval: t(`membership.interval.${plan.interval}`).toLocaleLowerCase(i18n.language), amount: formatMoney(plan.price, i18n.language, plan.currency) })}
      footer={<>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
        <Button variant="primary" icon={<CreditCard className="size-4" />} loading={start.isPending} onClick={submit} data-testid="membership-buyer-submit">
          {t('membership.toPayment')}
        </Button>
      </>}>
      <div className="flex flex-col gap-3" data-testid="membership-buyer">
        <FormError message={error ?? undefined} />
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('promotion.buyer.name')} required error={fieldErrors.name}>{(p) => <Input {...p} autoComplete="given-name" value={buyer.name} onChange={set('name')} />}</Field>
          <Field label={t('promotion.buyer.surname')} required error={fieldErrors.surname}>{(p) => <Input {...p} autoComplete="family-name" value={buyer.surname} onChange={set('surname')} />}</Field>
          <Field label={t('promotion.buyer.identityNumber')} required error={fieldErrors.identityNumber}
            hint={saved?.identityNumberMasked ? t('membership.identityAgain', { masked: saved.identityNumberMasked }) : undefined}>
            {(p) => <Input {...p} inputMode="numeric" maxLength={11} className="mono" value={buyer.identityNumber} onChange={set('identityNumber')} />}
          </Field>
          <Field label={t('promotion.buyer.phone')} required error={fieldErrors.phone}>{(p) => <Input {...p} type="tel" autoComplete="tel" value={buyer.phone} onChange={set('phone')} />}</Field>
          <Field label={t('promotion.buyer.address')} required error={fieldErrors.address} className="sm:col-span-2">
            {(p) => <Input {...p} autoComplete="street-address" value={buyer.address} onChange={set('address')} />}
          </Field>
          <Field label={t('promotion.buyer.city')} required error={fieldErrors.city}>{(p) => <Input {...p} autoComplete="address-level1" value={buyer.city} onChange={set('city')} />}</Field>
        </div>
      </div>
    </Dialog>
  );
}

function PlanCard({ plan, current, next, action }: { plan: MembershipPlanDto; current: boolean; next: boolean; action: ReactNode }) {
  const { t, i18n } = useTranslation();
  const perMonth = plan.interval === 'Yearly' ? Math.round((plan.price / 12) * 100) / 100 : null;
  return (
    <li className={cn('panel flex flex-col gap-3 p-4', current && 'border-accent/60 ring-1 ring-accent/40')} data-testid="membership-plan" data-plan={plan.id}
      data-interval={plan.interval}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{plan.name}</h3>
        <Badge tone="neutral">{t(`membership.interval.${plan.interval}`)}</Badge>
        {plan.yearlySaving != null && plan.yearlySaving > 0 && (
          <Badge tone="accent">{t('membership.saving', { percent: Math.round(plan.yearlySaving * 100) })}</Badge>
        )}
        {current && <Badge tone="trusted" icon={<BadgeCheck />}>{t('membership.currentPlan')}</Badge>}
        {next && <Badge tone="info" icon={<CalendarClock />}>{t('membership.nextPlanBadge')}</Badge>}
      </div>
      <div>
        <span className="text-2xl font-semibold tracking-[-0.01em]"><Money value={plan.price} currency={plan.currency} /></span>
        <span className="ml-1 text-sm text-ink-3">{t(`membership.per.${plan.interval}`)}</span>
        <p className="text-xs text-ink-3">
          {t('membership.vatIncluded')}
          {perMonth != null && ` · ${t('membership.perMonthApprox', { amount: formatMoney(perMonth, i18n.language, plan.currency) })}`}
        </p>
      </div>
      {plan.description && <p className="text-sm text-ink-2">{plan.description}</p>}
      {plan.features.length > 0 && (
        <ul className="flex flex-col gap-1.5 text-sm">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 size-4 shrink-0 text-trusted" aria-hidden />{f}</li>
          ))}
        </ul>
      )}
      <div className="mt-auto pt-1">{action}</div>
    </li>
  );
}

function PaymentsTable({ payments, from }: { payments: MembershipPaymentDto[]; from: 'orgMembership' | 'myMembership' }) {
  const { t, i18n } = useTranslation();
  const date = (v: string | null) => formatDate(v, i18n.language);
  return (
    <div className="overflow-x-auto">
      <table className="data-table" data-testid="membership-payments">
        <thead>
          <tr>
            <th className="w-32">{t('membership.paidAt')}</th>
            <th>{t('membership.plan')}</th>
            <th className="w-52">{t('membership.period')}</th>
            <th className="w-32 text-right">{t('membership.amount')}</th>
            <th className="w-40">{t('membership.invoice')}</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p.id}>
              <td>{date(p.paidAt)}</td>
              <td>{p.planName} <span className="text-ink-3">· {t(`membership.interval.${p.interval}`)}</span></td>
              <td className="text-ink-2">{date(p.periodStart)} – {date(p.periodEnd)}</td>
              <td className="text-right"><Money value={p.amount} currency={p.currency} /></td>
              <td>
                {p.invoiceId
                  ? <Link to={`/print/invoices/${p.invoiceId}?from=${from}`} className="mono text-accent-text hover:underline">{p.invoiceNumber ?? t('invoice.draft')}</Link>
                  : <span className="text-ink-3">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * An organization's or a tester's membership: where it stands, the plans (start, switch between monthly and yearly), cancelling,
 * and the payments with their invoices. Starting goes to the card provider's page and comes back with ?checkout=success|failed.
 */
export function MembershipPanel({ subject, page }: { subject: MembershipSubject; page: MembershipPageDto }) {
  const { t, i18n } = useTranslation();
  const date = (v: string | null) => (isForever(v) ? t('membership.forever') : formatDate(v, i18n.language));
  const s = page.status;
  const start = useStartMembership(subject);
  const cancel = useCancelMembership(subject);
  const change = useChangeMembershipPlan(subject);
  const [buyerFor, setBuyerFor] = useState<MembershipPlanDto | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [switchTo, setSwitchTo] = useState<MembershipPlanDto | null>(null);
  const intervals = [...new Set(page.plans.map((p) => p.interval))];
  const toggle = intervals.length > 1 && page.plans.length > 2;
  const [interval, setInterval] = useState<BillingInterval>(s.plan?.interval ?? 'Monthly');
  const shown = toggle ? page.plans.filter((p) => p.interval === interval) : page.plans;
  const active = s.state === 'Active';
  const blocked = page.blockers.length > 0;
  const org = subject.kind === 'org';

  const begin = (plan: MembershipPlanDto) => {
    if (!org) { setBuyerFor(plan); return; }
    start.mutate({ planId: plan.id }, { onSuccess: (r) => window.location.assign(r.checkoutUrl), onError: (e) => toastError(e) });
  };

  const actionFor = (plan: MembershipPlanDto): ReactNode => {
    if (!page.canManage) return null;
    const current = s.plan?.id === plan.id;
    if (active) {
      if (s.nextPlan?.id === plan.id)
        return <p className="text-xs text-ink-2">{t('membership.switchScheduled', { date: date(s.paidThrough) })}</p>;
      if (current && !s.nextPlan) return <Button className="w-full" disabled>{t('membership.currentPlan')}</Button>;
      return (
        <Button className="w-full" icon={<CalendarClock className="size-4" />} loading={change.isPending && change.variables === plan.id}
          onClick={() => (current ? change.mutate(plan.id, { onSuccess: () => toast.success(t('membership.keepDone')), onError: (e) => toastError(e) }) : setSwitchTo(plan))}
          data-testid="membership-switch">
          {current ? t('membership.keepPlan') : t('membership.switch')}
        </Button>
      );
    }
    const label = s.state === 'PastDue' ? t('membership.payNewCard') : t('membership.start');
    return (
      <Button variant="primary" className="w-full" icon={<CreditCard className="size-4" />} disabled={blocked}
        loading={start.isPending && start.variables?.planId === plan.id} onClick={() => begin(plan)} data-testid="membership-start">
        {label}
      </Button>
    );
  };

  const headIcon = s.state === 'PastDue' || s.state === 'Expired' || (s.state === 'Cancelled' && !s.entitled)
    ? <AlertTriangle className="size-5 text-suspicious" aria-hidden />
    : s.state === 'Trial' ? <Hourglass className="size-5 text-info" aria-hidden /> : <Sparkles className="size-5 text-accent-text" aria-hidden />;

  return (
    <div className="flex flex-col gap-5">
      <Section title={t('membership.statusTitle')}
        actions={page.canManage && (active || s.state === 'PastDue') && (
          <Button variant="ghost" size="sm" onClick={() => setCancelOpen(true)} data-testid="membership-cancel">{t('membership.cancel')}</Button>
        )}>
        <div className="flex items-start gap-3 p-4" data-testid="membership-status" data-state={s.state}>
          {headIcon}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <MembershipStateBadge state={s.state} />
              {s.state === 'Trial' && s.daysLeft != null && <span className="text-sm font-semibold">{t('membership.daysLeft', { count: s.daysLeft })}</span>}
            </div>
            <p className="mt-1.5 text-sm text-ink-2">{s.required ? statusLine(s, t, date) : t('membership.notRequired')}</p>
            {s.state === 'PastDue' && s.lastPaymentError && <p className="mt-1 text-sm text-suspicious">{t('membership.lastError', { error: s.lastPaymentError })}</p>}
            {s.state === 'Granted' && s.grantNote && <p className="mt-1 text-sm text-ink-3">{s.grantNote}</p>}
            {active && s.nextPlan && (
              <p className="mt-1 text-sm text-ink-2" data-testid="membership-next">
                {t('membership.nextPlan', { plan: `${s.nextPlan.name} · ${t(`membership.interval.${s.nextPlan.interval}`)}`, date: date(s.paidThrough), amount: formatMoney(s.nextPlan.price, i18n.language, s.nextPlan.currency) })}
              </p>
            )}
            {s.required && !s.entitled && <p className="mt-1 text-sm text-ink-2">{org ? t('membership.lockedOrg') : t('membership.lockedTester')}</p>}
            {!page.canManage && <p className="mt-2 text-xs text-ink-3">{t('membership.managersOnly')}</p>}
          </div>
        </div>
      </Section>

      {s.required && (
        <Section title={t('membership.plansTitle')} description={org ? t('membership.plansHintOrg') : t('membership.plansHintTester')}
          actions={toggle && (
            <Segmented<BillingInterval> size="sm" label={t('membership.period')} value={interval} onChange={setInterval}
              options={intervals.map((i) => ({ value: i, label: t(`membership.interval.${i}`) }))} />
          )}>
          <div className="flex flex-col gap-3 p-4">
            {page.blockers.includes('notConfigured') && <p className="text-sm text-suspicious" role="alert">{t('membership.blocker.notConfigured')}</p>}
            {page.blockers.includes('billing') && page.canManage && (
              <p className="text-sm text-suspicious" role="alert" data-testid="membership-billing-missing">
                {t('membership.blocker.billing')} <Link to="/org/billing" className="underline">{t('membership.fillBilling')}</Link>
              </p>
            )}
            {page.canManage && !active && s.state !== 'PastDue' && (s.state === 'Trial' || (s.state === 'Cancelled' && s.entitled) || s.state === 'Granted') && (
              <p className="text-xs text-ink-3">{t('membership.startsToday')}</p>
            )}
            {shown.length === 0 ? <p className="text-sm text-ink-3">{t('membership.noPlans')}</p> : (
              <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {shown.map((p) => (
                  <PlanCard key={p.id} plan={p} current={(active || s.state === 'PastDue') && s.plan?.id === p.id} next={active && s.nextPlan?.id === p.id} action={actionFor(p)} />
                ))}
              </ul>
            )}
            <p className="text-xs text-ink-3">{t('membership.autoRenew')}</p>
          </div>
        </Section>
      )}

      {page.payments.length > 0 && (
        <Section title={t('membership.paymentsTitle')} description={t('membership.paymentsHint')}>
          <PaymentsTable payments={page.payments} from={org ? 'orgMembership' : 'myMembership'} />
        </Section>
      )}

      {buyerFor && <BuyerDialog plan={buyerFor} saved={page.buyer} open={!!buyerFor} onOpenChange={(o) => !o && setBuyerFor(null)} />}
      <ConfirmDialog open={cancelOpen} onOpenChange={setCancelOpen} title={t('membership.cancelTitle')}
        description={t('membership.cancelBody', { date: date(s.paidThrough) })} confirmLabel={t('membership.cancelConfirm')} loading={cancel.isPending}
        onConfirm={() => cancel.mutate(undefined, {
          onSuccess: () => { setCancelOpen(false); toast.success(t('membership.cancelled')); },
          onError: (e) => toastError(e),
        })} />
      {switchTo && (
        <ConfirmDialog open={!!switchTo} onOpenChange={(o) => !o && setSwitchTo(null)} tone="primary" title={t('membership.switchTitle', { plan: switchTo.name })}
          description={t('membership.switchBody', {
            date: date(s.paidThrough), interval: t(`membership.interval.${switchTo.interval}`).toLocaleLowerCase(i18n.language),
            amount: formatMoney(switchTo.price, i18n.language, switchTo.currency),
          })}
          confirmLabel={t('membership.switch')} loading={change.isPending}
          onConfirm={() => change.mutate(switchTo.id, {
            onSuccess: () => { setSwitchTo(null); toast.success(t('membership.switchDone', { date: date(s.paidThrough) })); },
            onError: (e) => toastError(e),
          })} />
      )}
    </div>
  );
}

/** Greets the payer back from the card page (?checkout=success|failed) once, then drops the parameter. */
export function useCheckoutOutcome() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const outcome = params.get('checkout');
  useEffect(() => {
    if (!outcome) return;
    // Fixed ids: the effect may run twice in development; one toast either way.
    if (outcome === 'success') toast.success(t('membership.checkoutDone'), { id: 'membership-success' });
    else toast.error(t('membership.checkoutFailed'), { id: 'membership-failed' });
    const next = new URLSearchParams(params);
    next.delete('checkout');
    setParams(next, { replace: true });
  }, [outcome, params, setParams, t]);
}

/** Which membership the banner is about on this page: the current organization's (customer pages) or the tester's own (tester pages). */
function useBannerMembership(): { m: MeMembershipDto; manage: boolean } | null {
  const { data: me } = useMe();
  const { pathname } = useLocation();
  const { org } = useCurrentOrg();
  const area = areaOfPath(pathname);
  if (!me?.memberships) return null;
  if (area === 'customer' && org) {
    const m = me.memberships.find((x) => x.audience === 'Organization' && x.organizationId === org.id);
    return m ? { m, manage: canManage(org.role) } : null;
  }
  if (area === 'tester') {
    const m = me.memberships.find((x) => x.audience === 'Tester');
    return m ? { m, manage: true } : null;
  }
  return null;
}

/**
 * A strip above the page while a trial runs, a renewal failed, access is about to end or has ended. Hidden on the membership page itself
 * and while everything is in order.
 */
export function MembershipBanner() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const found = useBannerMembership();
  if (!found || pathname.endsWith('/membership')) return null;
  const { m, manage } = found;
  // A trial is the managers' business; the others hear about it only when work stops.
  if (!manage && m.state === 'Trial') return null;
  const date = formatDate(m.accessUntil, i18n.language);
  const tester = m.audience === 'Tester';
  let tone: 'info' | 'check' | 'suspicious';
  let text: string;
  switch (m.state) {
    case 'Trial':
      tone = (m.daysLeft ?? 0) <= 2 ? 'check' : 'info';
      text = t('membership.banner.Trial', { count: m.daysLeft ?? 0, date });
      break;
    case 'PastDue':
      tone = 'check';
      text = t('membership.banner.PastDue', { date });
      break;
    case 'Cancelled':
      if (m.entitled && (m.daysLeft ?? 99) > 7) return null;
      tone = m.entitled ? 'check' : 'suspicious';
      text = m.entitled ? t('membership.banner.Cancelled', { date }) : tester ? t('membership.banner.ExpiredTester') : t('membership.banner.Expired');
      break;
    case 'Expired':
      tone = 'suspicious';
      text = tester ? t('membership.banner.ExpiredTester') : t('membership.banner.Expired');
      break;
    default:
      return null;
  }
  const styles = {
    info: 'border-info/30 bg-info-soft text-info',
    check: 'border-check/30 bg-check-soft text-check',
    suspicious: 'border-suspicious/30 bg-suspicious-soft text-suspicious',
  }[tone];
  return (
    <div role={tone === 'suspicious' ? 'alert' : 'status'} data-testid="membership-banner" data-state={m.state}
      className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 text-sm sm:px-6', styles)}>
      {tone === 'info' ? <Hourglass className="size-4 shrink-0" aria-hidden /> : <AlertTriangle className="size-4 shrink-0" aria-hidden />}
      <span className="min-w-0 flex-1 text-ink">{text}</span>
      {manage
        ? <Link to={membershipPath(m)} className="font-semibold underline underline-offset-2">{m.state === 'Trial' ? t('membership.banner.choose') : t('membership.banner.open')}</Link>
        : <span className="text-xs text-ink-2">{t('membership.banner.askManager')}</span>}
    </div>
  );
}
